import { useEffect, useState } from 'react';

import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import useForm from 'dpl/shared/form/hooks/useForm';
import { CONSTRAINT_TYPES } from 'dpl/shared/validations/constraints';

import {
  quizSubmissionResourceDefinition,
  quizSectionMutationDefinition,
  quizSubmissionMutationDefinition
} from './queryDefinitions';

function getInitialFormState(section: any) {
  if (!section) {
    return {};
  }

  return section.questions.reduce(
    (formState: any, question: any) => ({
      ...formState,
      [question.input_name]: question.answer
    }),
    {}
  );
}

function getValidationSchema(section: any) {
  if (!section) {
    return {};
  }

  return section.questions.reduce((schema: any, question: any) => {
    const constraintType = (CONSTRAINT_TYPES as any)[
      question.validation_constraint
    ];

    if (!constraintType) {
      return schema;
    }

    return {
      ...schema,
      [question.input_name]: constraintType
    };
  }, {});
}

export default function useQuiz(
  submissionId: any,
  onAnsweredEvent?: (quiz: any, eventName: any) => void
) {
  /**
   * sectionIdx is used to determine which section to fetch from the API;
   * renderedSectionIdx is used to determine which section is ready to be
   * displayed. The latter can only be updated once the server responded with
   * the updated data.
   */
  const [sectionIdx, setSectionIdx] = useState<number | null>(null);
  const [renderedSectionIdx, setRenderedSectionIdx] = useState<number | null>(
    sectionIdx
  );

  const [quiz, setQuiz] = useState<any>(null);
  const [isQuizSubmitting, setIsQuizSubmitting] = useState(false);
  const loaded = Boolean(quiz) && renderedSectionIdx !== null;
  const section = loaded && quiz.sections[renderedSectionIdx!];

  // @ts-ignore
  const { value, isSuccess, updatedAt } = useResourceQuery(
    // FIXME: initial request fires twice. once w/ `section_idx=` when the
    // initial section idx is unknown, and second w/ `section_idx=<idx>` after
    // the first resolves.
    quizSubmissionResourceDefinition(submissionId, sectionIdx)
  );

  const { mutateAsync: answerSection } = useResourceMutation(
    quizSectionMutationDefinition(submissionId, section && section.uuid)
  );

  const { mutateAsync: updateQuizSubmission } = useResourceMutation(
    quizSubmissionMutationDefinition(submissionId)
  );

  /**
   * Upon first load of the quiz, set the sectionIdx to the first unanswered
   * section returned from the API. If all sections have been answered, render
   * the last section in the quiz
   */
  useEffect(() => {
    if (!loaded && isSuccess) {
      setSectionIdx(
        value.first_unanswered_section_idx ?? value.sections.length - 1
      );
    }
  }, [loaded, isSuccess]);

  useEffect(() => {
    if (isSuccess) {
      setQuiz(value);
      setRenderedSectionIdx(sectionIdx);
    }
  }, [updatedAt, isSuccess, sectionIdx]);

  const {
    handleSubmission,
    contextValue,
    isValid,
    isSubmitting,
    isDirty,
    reset
  } = useForm({
    onSubmit: async (formState: any) => {
      const sectionAnswers = Object.entries(formState).map(
        ([inputName, answer]) => ({
          input_name: inputName,
          value: answer
        }),
        []
      );

      const resp = await answerSection({
        body: {
          section: {
            answers: sectionAnswers
          }
        }
      });

      if (resp.data?.on_answered_event && onAnsweredEvent) {
        onAnsweredEvent(quiz, resp.data.on_answered_event);
      }
    },
    defaultFormState: getInitialFormState(section),
    mapFormStateToValidationSchema: () => getValidationSchema(section)
  });

  async function handleQuizUpdate(payload: any) {
    setIsQuizSubmitting(true);
    try {
      await updateQuizSubmission({ body: { quiz_submission: payload } });
    } finally {
      setIsQuizSubmitting(false);
    }
  }

  function handleQuizSubmission() {
    return handleQuizUpdate({ submit: true });
  }

  function handleQuizStart() {
    return handleQuizUpdate({ start: true });
  }

  function incrementSectionIdx() {
    setSectionIdx(Math.min(sectionIdx! + 1, quiz.sections.length - 1));
  }

  function decrementSectionIdx() {
    setSectionIdx(Math.max(sectionIdx! - 1, 0));
  }

  return {
    isValid,
    isSectionSubmitting: isSubmitting,
    isQuizSubmitting,
    isDirty,
    loaded,
    quiz,
    currentSection: section,
    currentSectionIdx: renderedSectionIdx,
    submitSection: handleSubmission,
    submitQuiz: handleQuizSubmission,
    startQuiz: handleQuizStart,
    incrementSectionIdx,
    decrementSectionIdx,
    resetFormState: () =>
      reset(getInitialFormState(quiz.sections[renderedSectionIdx!])),
    formContext: contextValue
  };
}
